import { useMemo, useState } from 'react'
import { useStore } from '../store/store'
import type { ModelInputs, PositionKind } from '../domain/types'
import { calibrationReport, estimateProbability, evaluateModel } from '../lib/math'
import { fmtPct, fmtSigned } from '../lib/format'
import { Button, Card, NumberField, PageHead, Pill, RangeField, SelectField, TextArea } from '../components/ui'
import { ActionPill } from '../components/widgets'
import { CalibrationChart } from '../components/charts'

const DEFAULT_INPUTS: ModelInputs = {
  baseProb: 0.5,
  recentForm: 0,
  historicalWinRate: 0.5,
  volatility: 0.3,
  marketMovement: 0,
  notes: '',
  confidence: 0.5,
  dataQuality: 0.6,
}

export function ModelLab() {
  const predictions = useStore((s) => s.modelPredictions)
  const addPrediction = useStore((s) => s.addModelPrediction)
  const resolve = useStore((s) => s.resolveModelPrediction)

  const [inputs, setInputs] = useState<ModelInputs>(DEFAULT_INPUTS)
  const [label, setLabel] = useState('')
  const [category, setCategory] = useState<PositionKind>('sportsbook')
  const [decimalOdds, setDecimalOdds] = useState(2)

  const set = (patch: Partial<ModelInputs>) => setInputs((i) => ({ ...i, ...patch }))
  const estimate = estimateProbability(inputs)
  const evaluation = evaluateModel(inputs, decimalOdds)

  const calib = useMemo(
    () =>
      calibrationReport(
        predictions.filter((p) => p.resolved && p.outcome !== undefined).map((p) => ({ prob: p.estProb, outcome: p.outcome as 0 | 1 })),
      ),
    [predictions],
  )

  const save = () => {
    addPrediction({
      label: label || `${category} model`,
      category,
      inputs,
      estProb: estimate.estProb,
      ciLow: estimate.ciLow,
      ciHigh: estimate.ciHigh,
      marketProb: evaluation.marketProb,
      edge: evaluation.edge,
      ev: evaluation.ev,
      action: evaluation.action,
      resolved: false,
    })
    setLabel('')
  }

  const unresolved = predictions.filter((p) => !p.resolved)

  return (
    <div className="stack">
      <PageHead title="Probability Model Lab">
        Build a simple model without code. Blend a base rate with form, history and market movement — and always carry an honest confidence interval.
      </PageHead>

      <div className="grid cols-3">
        <Card title="Model inputs" subtitle="Drag the sliders; everything updates live">
          <RangeField label="Base probability" value={inputs.baseProb} onChange={(v) => set({ baseProb: v })} display={fmtPct(inputs.baseProb, 0)} />
          <RangeField label="Historical win rate" value={inputs.historicalWinRate} onChange={(v) => set({ historicalWinRate: v })} display={fmtPct(inputs.historicalWinRate, 0)} />
          <RangeField label="Recent form" value={inputs.recentForm} onChange={(v) => set({ recentForm: v })} min={-1} max={1} display={fmtSigned(inputs.recentForm, 2)} />
          <RangeField label="Market movement (toward you)" value={inputs.marketMovement} onChange={(v) => set({ marketMovement: v })} min={-1} max={1} display={fmtSigned(inputs.marketMovement, 2)} />
          <RangeField label="Volatility / uncertainty" value={inputs.volatility} onChange={(v) => set({ volatility: v })} display={fmtPct(inputs.volatility, 0)} />
          <RangeField label="Confidence" value={inputs.confidence} onChange={(v) => set({ confidence: v })} display={fmtPct(inputs.confidence, 0)} />
          <RangeField label="Data quality" value={inputs.dataQuality} onChange={(v) => set({ dataQuality: v })} display={fmtPct(inputs.dataQuality, 0)} />
          <TextArea label="Notes (injuries, news, context)" value={inputs.notes} onChange={(v) => set({ notes: v })} rows={2} />
        </Card>

        <Card title="Estimate" action={<ActionPill action={evaluation.action} />}>
          <div className="stat-label">Estimated probability</div>
          <div className="stat-value">{fmtPct(estimate.estProb, 1)}</div>
          <div className="stat-sub">90% range {fmtPct(estimate.ciLow, 0)} – {fmtPct(estimate.ciHigh, 0)}</div>
          <div className="divider" />
          <SelectField label="Compare to market" value={category} onChange={setCategory} options={[{ value: 'sportsbook', label: 'Sportsbook' }, { value: 'prediction', label: 'Prediction market' }, { value: 'stock', label: 'Stock' }]} />
          <NumberField label="Market decimal odds" value={decimalOdds} onChange={setDecimalOdds} step={0.01} min={1.01} />
          <div className="kv"><span className="k">Market implied</span><span className="v">{fmtPct(evaluation.marketProb, 1)}</span></div>
          <div className="kv"><span className="k">Edge</span><span className={`v ${evaluation.edge >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(evaluation.edge * 100, 1)}%</span></div>
          <div className="kv"><span className="k">EV per $1</span><span className={`v ${evaluation.ev >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(evaluation.ev, 3)}</span></div>
          <div className="kv"><span className="k">Uncertainty</span><span className="v">{fmtPct(estimate.uncertainty, 0)}</span></div>
        </Card>

        <Card title="Save & suggested action">
          <p className="muted" style={{ marginTop: 0 }}>
            {evaluation.action === 'strong' && 'Strong watch — a clear edge with acceptable uncertainty.'}
            {evaluation.action === 'small' && 'Small position — real but modest edge.'}
            {evaluation.action === 'paper' && 'Paper only — the interval is too wide to commit real capital.'}
            {evaluation.action === 'skip' && 'Skip — no edge once uncertainty is respected.'}
          </p>
          <input className="input" placeholder="Label (e.g. Lakers ML)" value={label} onChange={(e) => setLabel(e.target.value)} style={{ marginBottom: 10 }} />
          <Button variant="primary" onClick={save}>Save prediction for calibration</Button>
          <p className="help" style={{ marginTop: 10 }}>
            Saved predictions feed the calibration tracker below. Resolve them later to learn whether your probabilities are honest.
          </p>
        </Card>
      </div>

      <div className="grid cols-2">
        <Card title="Calibration tracker" subtitle={`${calib.count} resolved predictions · Brier ${calib.brier.toFixed(3)} · ECE ${(calib.ece * 100).toFixed(1)}%`}>
          <div className="row between" style={{ alignItems: 'center' }}>
            <div>
              <div className="stat-label">Calibration score</div>
              <div className="stat-value">{calib.score.toFixed(0)}</div>
            </div>
            <Pill tone={Math.abs(calib.overconfidenceBias) < 0.05 ? 'green' : calib.overconfidenceBias > 0 ? 'red' : 'amber'}>
              {calib.overconfidenceBias > 0 ? 'Overconfident' : calib.overconfidenceBias < 0 ? 'Underconfident' : 'Balanced'} {fmtSigned(calib.overconfidenceBias * 100, 0)}%
            </Pill>
          </div>
          <p className="muted">{calib.verdict}</p>
          <CalibrationChart buckets={calib.buckets} />
          <div className="legend"><span>Dot size = sample count · diagonal = perfect calibration</span></div>
        </Card>

        <Card title="Unresolved predictions" subtitle="Resolve to improve your calibration score">
          {unresolved.length === 0 ? (
            <div className="empty">Nothing pending. Save a prediction to start tracking.</div>
          ) : (
            <div className="scroll-y">
              <table className="table">
                <thead><tr><th>Label</th><th>Prob</th><th>Edge</th><th>Resolve</th></tr></thead>
                <tbody>
                  {unresolved.slice(0, 30).map((p) => (
                    <tr key={p.id}>
                      <td>{p.label}</td>
                      <td className="mono">{fmtPct(p.estProb, 0)}</td>
                      <td className={`mono ${p.edge >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(p.edge * 100, 0)}%</td>
                      <td className="nowrap">
                        <button className="btn sm" onClick={() => resolve(p.id, 1)}>Hit</button>{' '}
                        <button className="btn sm" onClick={() => resolve(p.id, 0)}>Miss</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
